import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const PatternPerformanceDashboard = () => {
  const [allPatterns, setAllPatterns] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [patternStats, setPatternStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysBack, setDaysBack] = useState(90);
  const [updating, setUpdating] = useState(false);

  const fetchAllPatterns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/tools/pattern-performance/all?days_back=${daysBack}`);
      setAllPatterns(response.data || []);
    } catch (err) {
      console.error('Error fetching pattern performance:', err);
      setError(err.response?.data?.detail || 'Failed to fetch pattern performance data');
    } finally {
      setLoading(false);
    }
  }, [daysBack]);

  useEffect(() => {
    fetchAllPatterns();
  }, [fetchAllPatterns]);

  const fetchPatternStats = async (patternName, timeframe) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/api/v1/tools/pattern-performance?pattern_name=${encodeURIComponent(patternName)}&timeframe=${encodeURIComponent(timeframe)}&days_back=${daysBack}`
      );
      setPatternStats(response.data);
      setSelectedPattern({ patternName, timeframe });
    } catch (err) {
      console.error('Error fetching pattern stats:', err);
      setError(err.response?.data?.detail || 'Failed to fetch pattern statistics');
    } finally {
      setLoading(false);
    }
  };

  const updatePendingSignals = async () => {
    try {
      setUpdating(true);
      setError(null);
      const response = await api.post('/api/v1/tools/update-pending-signals');
      alert(`Updated ${response.data.updated_count} pending signals out of ${response.data.total_pending}`);
      // Refresh data
      fetchAllPatterns();
      if (selectedPattern) {
        fetchPatternStats(selectedPattern.patternName, selectedPattern.timeframe);
      }
    } catch (err) {
      console.error('Error updating pending signals:', err);
      setError(err.response?.data?.detail || 'Failed to update pending signals');
    } finally {
      setUpdating(false);
    }
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 0.70) return 'text-green-600';
    if (rate >= 0.60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProfitFactorColor = (factor) => {
    if (factor >= 1.5) return 'text-green-600';
    if (factor >= 1.0) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading && allPatterns.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pattern performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pattern Performance Dashboard</h1>
        <p className="text-gray-600">Track and analyze pattern success rates, profit factors, and performance metrics</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Controls */}
      <div className="mb-6 flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Days Back:</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
        </div>
        <button
          onClick={updatePendingSignals}
          disabled={updating}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {updating ? 'Updating...' : 'Update Pending Signals'}
        </button>
        <button
          onClick={fetchAllPatterns}
          disabled={loading}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {allPatterns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Total Patterns</p>
            <p className="text-2xl font-bold text-gray-900">{allPatterns.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Total Signals</p>
            <p className="text-2xl font-bold text-gray-900">
              {allPatterns.reduce((sum, p) => sum + (p.total_signals || 0), 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Avg Success Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {(
                allPatterns.reduce((sum, p) => sum + (p.success_rate || 0), 0) / allPatterns.length
              ).toFixed(1)}%
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Avg Profit Factor</p>
            <p className="text-2xl font-bold text-gray-900">
              {(
                allPatterns.reduce((sum, p) => sum + (p.profit_factor || 0), 0) / allPatterns.length
              ).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* All Patterns Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">All Patterns Performance</h2>
          </div>
          <div className="overflow-x-auto">
            {allPatterns.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No pattern performance data available yet.</p>
                <p className="text-sm mt-2">Signals will appear here once they are generated and tracked.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Pattern</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Timeframe</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Signals</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Success</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">P/F</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allPatterns.map((pattern, index) => (
                    <tr
                      key={index}
                      onClick={() => fetchPatternStats(pattern.pattern_name, pattern.timeframe)}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{pattern.pattern_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{pattern.timeframe}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900">{pattern.total_signals}</td>
                      <td className={`px-4 py-3 text-sm text-center font-semibold ${getSuccessRateColor(pattern.success_rate)}`}>
                        {(pattern.success_rate * 100).toFixed(1)}%
                      </td>
                      <td className={`px-4 py-3 text-sm text-center font-semibold ${getProfitFactorColor(pattern.profit_factor)}`}>
                        {pattern.profit_factor.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Selected Pattern Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedPattern ? `${selectedPattern.patternName} (${selectedPattern.timeframe})` : 'Pattern Details'}
            </h2>
          </div>
          <div className="p-4">
            {!selectedPattern ? (
              <div className="text-center text-gray-500 py-8">
                <p>Select a pattern from the table to view detailed statistics</p>
              </div>
            ) : loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : patternStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Total Signals</p>
                    <p className="text-lg font-bold text-gray-900">{patternStats.total_signals}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Completed</p>
                    <p className="text-lg font-bold text-gray-900">{patternStats.completed_signals}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Pending</p>
                    <p className="text-lg font-bold text-gray-900">{patternStats.pending_signals}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Wins</p>
                    <p className="text-lg font-bold text-green-600">{patternStats.wins}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Losses</p>
                    <p className="text-lg font-bold text-red-600">{patternStats.losses}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Success Rate</p>
                    <p className={`text-lg font-bold ${getSuccessRateColor(patternStats.success_rate)}`}>
                      {(patternStats.success_rate * 100).toFixed(1)}%
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Win Rate</p>
                    <p className="text-lg font-bold text-gray-900">
                      {(patternStats.win_rate * 100).toFixed(1)}%
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Profit Factor</p>
                    <p className={`text-lg font-bold ${getProfitFactorColor(patternStats.profit_factor)}`}>
                      {patternStats.profit_factor.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Performance Metrics</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Profit:</span>
                      <span className="font-medium text-green-600">
                        {patternStats.avg_profit > 0 ? '+' : ''}{patternStats.avg_profit.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Loss:</span>
                      <span className="font-medium text-red-600">
                        {patternStats.avg_loss.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Holding Period:</span>
                      <span className="font-medium text-gray-900">
                        {Math.round(patternStats.avg_holding_period_minutes / 60)} hours
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>Failed to load pattern statistics</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternPerformanceDashboard;

